import * as React from 'react'
import { ReactElement, RefObject, useMemo, useState } from 'react'
import {
    Button,
    makeStyles,
    mergeClasses,
    Tag,
    TagPicker,
    TagPickerControl,
    TagPickerGroup,
    TagPickerInput,
    TagPickerList,
    TagPickerOption,
    TagPickerProps,
    Tooltip,
} from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import { TagPickerOnOptionSelectData } from '@fluentui/react-tag-picker'
import { useBoolean } from '@fluentui/react-hooks'
import { DismissRegular } from '@fluentui/react-icons'

export const useBaseStyles = makeStyles({
    control: { minWidth: '100px' },
    listbox: { maxHeight: '400px', minWidth: '300px', lineBreak: 'anywhere', textWrapMode: 'nowrap' },
    input: {},
    secondaryButton: { alignItems: 'flex-start' },
    optionNoAvatar: { display: 'flex' },
    archived: { textDecoration: 'line-through' },
})

export type BaseTagPickerProps<E, G = E> = Omit<TagPickerProps, 'value' | 'children' | 'onOptionSelect'> & {
    entities: E[]
    onOptionsSelect: (e: Event | React.SyntheticEvent, selectedOptions: string[], selectedEntities: E[]) => void

    filter?: (entity: E) => boolean
    alphabetical?: boolean
    sort?: (a: E, b: E) => number

    placeholderPrefixKey?: string
    placeholderKey?: string
    required?: boolean
    singleSelect?: boolean
    autoFocus?: boolean

    isGroup?: (entity: E) => boolean
    getGroupEntities?: (group: G) => E[]
    getGroupValue?: (entity: G) => string
    getGroupLabel?: (entity: G) => string // A short-form label, used to fill the Avatar and the first part of the label.
    getGroupSecondaryContent?: (entity: G) => string // A second optional label, used as appendix in the list.
    getGroupClassName?: (entity: G) => string

    getEntityLabel: (entity: E) => string // A short-form label.
    getEntitySecondaryContent?: (entity: E) => string // A second optional label, appended to getEntityLabel.
    getEntityValue: (entity: E) => string // The key, most often the ID.
    getEntitySearchValue?: (entity: E) => string // The value with which entities are filtered by.
    getEntityTagLabel?: (entity: E) => string // The key used to generate the tag text in the input.
    getEntityTagSecondaryContent?: (entity: E) => string // A second optional tag label, used to show a tooltip.
    getEntityClassName?: (entity: E) => string[] // An optional list of classNames for TagPickerOption.

    componentRef?: RefObject<HTMLInputElement>
    styles?: Partial<Record<'control' | 'listbox' | 'input' | 'secondaryButton' | 'optionNoAvatar' | 'archived', string>>
}

function findEntities<E, G>(entities: E[], selectedEntities: string[], props: BaseTagPickerProps<E, G>): E[] {
    return entities
        .map(e => (props.isGroup?.(e) ? props.getGroupEntities?.(e as any) : [e]))
        .reduce((a, b) => a.concat(b), [])
        .filter(entity => selectedEntities.indexOf(props.getEntityValue(entity)) >= 0)
}

function applyOnOptionSelect<E, G>(e: Event | React.SyntheticEvent, data: TagPickerOnOptionSelectData, props: BaseTagPickerProps<E, G>): void {
    let selectedOptions = data.selectedOptions.filter(option => !!option)
    if (props.singleSelect) {
        selectedOptions = selectedOptions.filter(option => option === data.value)
    } else {
        selectedOptions = selectedOptions?.sort((a, b) =>
            props.entities.findIndex(e => props.getEntityValue(e) === a) < props.entities.findIndex(e => props.getEntityValue(e) === b) ? -1 : 1,
        )
    }

    props.onOptionsSelect(e, selectedOptions, findEntities(props.entities, selectedOptions || [], props))
}

function TagPickerTag<E, G = E>(props: BaseTagPickerProps<E, G> & { option: E }): ReactElement {
    if (props.getEntityTagSecondaryContent) {
        return (
            <Tag shape="rounded" value={props.getEntityValue(props.option)} dismissible={!props.required || props.selectedOptions?.length === 0}>
                <Tooltip content={props.getEntityTagSecondaryContent?.(props.option)} relationship="label" withArrow>
                    <div>{props.getEntityTagLabel?.(props.option) ?? props.getEntityLabel(props.option)}</div>
                </Tooltip>
            </Tag>
        )
    }

    return (
        <Tag shape="rounded" value={props.getEntityValue(props.option)} dismissible={!props.required || props.selectedOptions?.length === 0}>
            <div>{props.getEntityTagLabel?.(props.option) ?? props.getEntityLabel(props.option)}</div>
        </Tag>
    )
}

export default function BaseTagPicker<E, G = E>(props: BaseTagPickerProps<E, G>): ReactElement {
    const styles = useBaseStyles()
    const { t: translate } = useTranslation()

    const entities: E[] = useMemo(
        () =>
            (props.entities || [])
                .filter(entity => !props.filter || props.filter(entity) || props.selectedOptions?.some(id => id === entity.toString()))
                .sort((a, b) =>
                    props.alphabetical ? props.getEntityLabel(a).localeCompare(props.getEntityLabel(b)) : props.sort ? props.sort(a, b) : 0,
                ),
        [props.entities],
    )
    const selectedEntities: E[] = useMemo(() => findEntities(entities, props.selectedOptions || [], props), [props.selectedOptions, entities])
    const placeholder: string = useMemo(() => {
        if (selectedEntities.length > 0) {
            return undefined
        }

        const prefix = props.placeholderPrefixKey === '' ? '' : translate(props.placeholderPrefixKey ?? 'base.tagpicker.placeholder.prefix')
        const label =
            props.placeholderKey === ''
                ? ''
                : translate(props.placeholderKey ?? 'base.tagpicker.placeholder.option', {
                      count: props.singleSelect ? 1 : 100,
                  })

        return `${prefix} ${label}`
    }, [props.placeholderPrefixKey, props.placeholderKey, selectedEntities])

    const [query, setQuery] = useState<string>('')
    const queriedEntities: E[] = useMemo(() => {
        return (entities || []).filter(
            entity =>
                query.length === 0 ||
                props.getEntitySearchValue?.(entity)?.toLowerCase().indexOf(query?.toLowerCase()) >= 0 ||
                props.getEntityLabel(entity).toLowerCase().indexOf(query?.toLowerCase()) >= 0 ||
                props.getEntitySecondaryContent?.(entity)?.toLowerCase().indexOf(query?.toLowerCase()) >= 0,
        )
    }, [props.entities, query])

    /*
     * Control whether this TagPicker is focused or not. This prevents Enters in Input components from removing selected items, but still allows
     * users to add/remove tags.
     */
    const [focused, { setTrue: setFocused, setFalse: setUnfocused }] = useBoolean(false)

    return (
        <TagPicker
            selectedOptions={props.selectedOptions}
            {...props}
            onOptionSelect={(e, data) => {
                if (focused) {
                    setQuery('')
                    applyOnOptionSelect(e, data, props)
                }
            }}
        >
            <TagPickerControl
                className={mergeClasses(styles.control, props.styles?.control)}
                secondaryAction={
                    !props.required &&
                    props.selectedOptions?.length > 0 && (
                        <Button
                            className={(props.styles || styles).secondaryButton}
                            icon={<DismissRegular />}
                            appearance="transparent"
                            disabled={props.disabled}
                            onClick={e => {
                                setQuery('')
                                props.onOptionsSelect(e, [], [])
                            }}
                        />
                    )
                }
                onFocus={setFocused}
                onBlur={setUnfocused}
            >
                <TagPickerGroup>
                    {selectedEntities.map(option => (
                        <TagPickerTag key={props.getEntityValue(option)} {...props} option={option} />
                    ))}
                </TagPickerGroup>
                <TagPickerInput
                    className={mergeClasses(styles.input, props.styles?.input)}
                    autoFocus={props.autoFocus}
                    placeholder={placeholder}
                    value={query}
                    onChange={e => setQuery(e.target.value)}
                    ref={props.componentRef}
                />
            </TagPickerControl>
            {/*This style assumes we have no avatar. Once we introduce an avatar we need to make this*/}
            <TagPickerList className={mergeClasses(styles.listbox, props.styles?.listbox)} multiselect={!props.singleSelect}>
                {queriedEntities
                    .filter(option => !selectedEntities.includes(option))
                    .map((option: E, index: number) => (
                        <TagPickerOption
                            key={index}
                            value={props.getEntityValue(option)}
                            secondaryContent={props.getEntitySecondaryContent?.(option)}
                            className={mergeClasses(
                                styles.optionNoAvatar,
                                props.styles?.optionNoAvatar,
                                ...(props.getEntityClassName ? props.getEntityClassName(option) : []),
                            )}
                        >
                            {props.getEntityLabel(option)}
                        </TagPickerOption>
                    ))}
            </TagPickerList>
        </TagPicker>
    )
}
